import React from 'react'
import { twMerge } from 'tailwind-merge'
import { useConfig } from 'msp-integrations'
import Icons from '@atoms/Icons/Icons'
import * as Styles from './Button.styles'
import { ButtonProps } from './Button.types'

const Button = ({
  onClick,
  type = 'primary',
  variant = 'default',
  iconName,
  iconPosition = 'left',
  isDisabled,
  isLoading,
  loadingContent,
  className,
  isSubmit,
  children,
  isDarkMode = false,
  deactivateTransition = false,
  iconColor = '',
  strokeColor = '',
  ariaLabel = ''
}: ButtonProps) => {
  const isSquare = type === 'tertiary'
  const isDefault = variant === 'default'
  const skin = Styles.skin[type]
  const layout = Styles.layouts[type][variant]
  const {
    theme: {
      font,
      mspComponents: { button }
    }
  } = useConfig()
  const defaultColors =
    button?.layoutColors?.getColors?.(isDarkMode) ||
    button?.layoutColors ||
    Styles.buttonLayoutColors
  const backgroundTransitionDefaultColors =
    button?.backgroundTransitionColors || Styles.backgroundTransitionColor
  const contentDefaultColors = button?.contentColors || Styles.contentColor
  const buttonShape = isSquare ? 'square' : 'default'
  const iconSize = Styles.iconSizes[buttonShape].md
  const buttonType = (isSubmit && 'submit') || 'button'
  const showLoadingContent = isLoading && loadingContent

  const layoutButton = `${layout.button} ${
    isSquare && isDefault ? defaultColors[buttonShape] : defaultColors[variant]
  }`
  const layoutBackgroundTransition = `${layout.backgroundTransition} ${
    isDefault
      ? backgroundTransitionDefaultColors[buttonShape]
      : backgroundTransitionDefaultColors.outline
  }`
  const layoutContentDiv = `${layout.contentDiv} ${
    isDefault ? contentDefaultColors[buttonShape] : contentDefaultColors.outline
  }`

  const getButtonIcon = (position: string) => {
    const color = showLoadingContent && 'transparent'
    return (
      iconName &&
      position === iconPosition && (
        <div
          className={twMerge(
            buttonShape === 'default' &&
              (position === 'left' ? 'pr-[12px]' : 'pl-[12px]')
          )}
        >
          <Icons
            iconName={iconName}
            height={iconSize.h}
            width={iconSize.w}
            stroke={strokeColor}
            color={iconColor || color}
          />
        </div>
      )
    )
  }

  return (
    <button
      type={buttonType}
      onClick={onClick}
      className={`${font.wide} ${skin} ${layoutButton} ${
        deactivateTransition || layoutBackgroundTransition
      } ${className} ${isDisabled ? 'cursor-not-allowed' : ''}`}
      disabled={isDisabled || isLoading}
      aria-label={ariaLabel}
    >
      <div
        className={`flex flex-row ${deactivateTransition || layoutContentDiv}`}
      >
        {showLoadingContent && (
          <span className={twMerge(Styles.centerLoadingContent)}>
            {loadingContent}
          </span>
        )}
        {getButtonIcon('left')}
        <span
          className={twMerge(
            'flex items-center whitespace-break-spaces text-center align-middle uppercase',
            showLoadingContent && 'text-transparent'
          )}
        >
          {children}
        </span>
        {getButtonIcon('right')}
      </div>
    </button>
  )
}

export default Button
